import * as api from "../../utils/api";
import imageHelper from "../../helpers/image.helper";
import Creative from "../Creative";
import {
  creativeTimeoutPromise,
  defaultHandlerCatch,
  defaultHandlerResolver,
  promisifyImage,
  waitTaskHelper
} from "./helpers";
import {photolabTask} from "../api";
import {assetUrl} from "../../utils/etc";
import analyticsHelper from "../../helpers/analytics.helper";
import {PhotolabTaskBuilder} from "@vicman-wi/photolab-api-js";

const tasksCache = {};

function getResultUrlFromTask(task) {
  if (task.requestId) {
    return task.resultUrl
  } else {
    return task.result.url;
  }
}

function templateStep(processing, creative, stepIndex, stepConfig) {
  if (!stepConfig.id) {
    return Promise.reject("No template ID parameter.");
  }

  const templateParams = stepConfig.templateParams || {};
  if (templateParams.gender && templateParams.gender[0] === "#") {
    const genderTask = creative.getTask("s" + templateParams.gender.substring(1));
    if (genderTask) {
      templateParams.gender = genderTask.gender === "male" ? "man" : "woman";
    } else {
      delete templateParams.gender;
    }
  }

  const taskConfigBuilder = new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addCollageMethod({template_name: stepConfig.id, ...templateParams});

  // @ - original photo
  // # - prev step result
  // #N - N step result, todo
  (stepConfig.images || [{src: "#"}]).forEach((image) => {
    const source = image.src || "#";
    let imageUrl;

    if (source === "@") {
      imageUrl = processing.file.url;
    } else if (source === "#") {
      if (stepIndex === 0) {
        imageUrl = processing.file.url;
      } else {
        imageUrl = getResultUrlFromTask(creative.getTask("s" + (stepIndex-1)));
      }
    } else {
      imageUrl = source;
    }

    const altBody = (image.useAltBody && processing.maskFile) ? processing.maskFile.url : "";
    const fileName = altBody.split("/").pop();
    const hash = fileName.substring(0, fileName.lastIndexOf("."));

    taskConfigBuilder.addImage({
      url: imageUrl + (hash.length ? "?" + hash : ""),
      rect: "",
      rotation: 0,
      flip: 0,
      alt_body: altBody
    });
  });

  const taskConfig = taskConfigBuilder.build()
  const taskCacheKey = JSON.stringify(taskConfig);
  const taskTimeout = stepConfig.getResultTimeout || 1000;
  const taskInterval = stepConfig.getResultInterval || 1000;

  if (!tasksCache[taskCacheKey]) {
    tasksCache[taskCacheKey] = photolabTask(taskConfig, {
      timeout: taskTimeout,
      interval: taskInterval,
    })
  }

  if (window.clientConfig.isDebug) {
    creative.setTaskConfig("s" + stepIndex, taskConfig);
  }

  return tasksCache[taskCacheKey].then((taskResult) => {
    creative.setTask("s" + stepIndex, taskResult);

    if (typeof stepConfig.setAsFile === "string" && stepConfig.setAsFile.length > 0) {
      creative.setFile(stepConfig.setAsFile, taskResult.resultUrl);
    }

    return taskResult;
  }).catch((err) => {
    if (err.name === "PhotolabTaskError" && err.code === -1028) {
      if (stepConfig.skipOnMultifaceError) {
        // const steps = creative.getExtra(Creative.EXTRA_COMBO_STEPS);
        // steps.splice(stepIndex, 1);
        // const name = steps.map((step) => step.id).join("_");
        //
        // creative.setExtra(Creative.EXTRA_NAME, name);

        const imageUrl = stepIndex === 0
          ? processing.file.url
          : getResultUrlFromTask(creative.getTask("s" + (stepIndex-1)));

        const result = {
          skipped: "skipOnMultifaceError",
          result: {url: imageUrl}
        };

        creative.setTask("s" + stepIndex, result);
        return result;
      }

      if (stepConfig.fallbackId) {
        const newStepConfig = JSON.parse(JSON.stringify(stepConfig));
        newStepConfig.id = newStepConfig.fallbackId;
        delete newStepConfig.fallbackId;
        return templateStep(processing, creative, stepIndex, newStepConfig);
      }
    }

    delete tasksCache[taskCacheKey];
    throw err;
  });
}

function apiWatermarkStep(processing, creative, stepIndex, stepConfig) {
  const imageUrl = getResultUrlFromTask(creative.getTask("s" + (stepIndex-1)));

  const watermarkConfig = {
    "content_url": assetUrl("assets/images/watermarks/default_4.png"),
    "position": "bottom-right",
    "x": "3%",
    "y": "2%",
    "percentage": 30,
  };

  return api.createTask("creative_layers", {content_url: imageUrl, layers: [watermarkConfig]})
    .then((taskResult) => waitTaskHelper(creative, "template_store", taskResult, 1000))
    .then((taskResult) => {
      creative.setTask("s" + stepIndex, taskResult);

      if (typeof stepConfig.setAsFile === "string" && stepConfig.setAsFile.length > 0) {
        creative.setFile(stepConfig.setAsFile, taskResult.result.url);
      }

      return taskResult;
    });
}

export function addWatermark(source, watermark, watermarkConfig) {
  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = source.height;

  const canvasCtx = canvas.getContext("2d");
  canvasCtx.drawImage(source, 0, 0);

  const watermarkWidth = Math.round(canvas.width / 100 * watermarkConfig.percentage);
  const watermarkHeight = watermarkWidth * watermark.height / watermark.width;

  const watermarkPosX = canvas.width - (watermarkWidth + Math.round(watermarkConfig.x * (canvas.width / 100)));
  const watermarkPosY = canvas.height - (watermarkHeight + Math.round(watermarkConfig.y * (canvas.height / 100)));

  canvasCtx.drawImage(watermark, watermarkPosX, watermarkPosY, watermarkWidth, watermarkHeight);

  return canvas;
}

export function watermarkStep(processing, creative, stepConfig) {
  analyticsHelper.frontendWatermarkStarted();

  const imageUrl = creative.getFile("raw");
  // const fileName = imageUrl.substring(imageUrl.lastIndexOf('/')+1);
  const watermarkConfig = stepConfig.watermark;

  return Promise.race([
    creativeTimeoutPromise(5000),
    Promise.all([
      promisifyImage(imageUrl, true),
      promisifyImage(watermarkConfig.url, true),
    ])
  ])
  .then(([source, watermark]) => {
    const canvas = addWatermark(source, watermark, watermarkConfig);

    return canvas.toDataURL("image/jpeg", 1.0);
  })
  .then((result) => {
    return imageHelper.store({
      file: result,
      disk: "ph_temp",
    });
  })
  .then((result) => {
    analyticsHelper.frontendWatermarkProcessed();
    creative.setFile(
      stepConfig.setAsFile || "raw_watermark",
      result.image_url
    );
  })
  .catch((error) => {
    analyticsHelper.frontendWatermarkFailed(error);

    throw error;
  });
}

/**
 * @param {Processing} processing
 * @param {Creative} creative
 */
export default (processing, creative) => {
  const steps = creative.getExtra(Creative.EXTRA_COMBO_STEPS);

  function waitChain(index) {
    return new Promise((resolve, reject) => {
      const step = steps[index];
      let stepHandler = null;

      switch (step.type || "template") {
        case "template": {
          stepHandler = templateStep(processing, creative, index, step);
          break;
        }
        case "api_watermark": {
          stepHandler = apiWatermarkStep(processing, creative, index, step);
          break;
        }
        default: {
          throw new Error(`Unrecognized combo step: '${step.type}'.`);
        }
      }

      return stepHandler
        .then((res) => steps[index + 1] ? waitChain(index + 1) : res)
        .then(resolve)
        .catch(reject);
    });
  }

  const timeoutMs = creative.getExtra(
    Creative.EXTRA_PROCESSING_TIMEOUT,
    window.appConfig.processings.creativeTimeout
  );

  return new Promise((resolve, reject) => {
    // todo остановить waitChain
    Promise.race([
      creativeTimeoutPromise(timeoutMs),
      waitChain(0),
    ])
    .then(() => {
      const file = creative.getFile("raw_watermark") || creative.getFile("raw");

      return promisifyImage(file)
        .then(() => creative.markAsProcessed(file));
    })
    .then(defaultHandlerResolver(creative, resolve))
    .catch(defaultHandlerCatch(creative, reject));
  });
}
